import React, { useContext, useEffect, useState } from 'react';

import { toJS } from 'mobx';
import { MobXProviderContext, observer } from 'mobx-react';

import classNames from 'classnames';

import { kebabCase } from 'lodash';

import '../../css/reports/ReportHideColumnsModal.less';

import { SatCoreComponent, SatCoreRegister } from '../../SatCoreRegistry';

import PopupService from '../../services/PopupService';
import ReportIdentityService from '../../services/reports/ReportIdentityService';
import { REPORT_FACULTY_TABLE_CELL_LABEL_KEY } from '../../services/reports/ReportConstants';

const ReportHideColumnsModal = observer((props = {}) => {
  const {
    buttonKeyCancelLabel = 'cancel',
    buttonKeyConfirmLabel = 'hideColumns',
    className = 'ReportHideColumnsModal',
    closeOnDimmerClick = false,
    closeOnEscape = true,
    disableKeystroke = false,
    disableMove = false,
    disableResize = true,
    initHeight,
    initWidth,
    minHeight = 520,
    minWidth = 465,
    onOpen = () => {},
    onRequestClose,
    open: isOpen,
    overlay = 'medium',
    t, top = window.innerHeight / 6
  } = props;

  const {
    reportIdentityManager,
    reportOverrideManager,
    reportTableManager
  } = useContext(MobXProviderContext);

  const {
    activeReportFacultyType,
    isFacultySchoolReport,
    reportInfoClassNames
  } = reportIdentityManager;

  const {
    allowReportHideColumnsResetAllAsUnselectedAction,
    allowReportHideColumnsResetAllToOriginalSelectionsAction
  } = reportOverrideManager;

  const [isLoadingReportHideColumnsModal, setIsLoadingReportHideColumnsModal] = useState(false);

  const [originalHiddenReportTableFacultyCellsMap, setOriginalHiddenReportTableFacultyCellsMap] = useState(new Map());

  const GenericModal = SatCoreComponent('GenericModal');
  const IndeterminateCheckbox = SatCoreComponent('IndeterminateCheckbox');

  useEffect(() => {
    if (isOpen) {
      const hiddenReportTableFacultyCellsMap = toJS(reportTableManager.hiddenReportTableFacultyCellsMap);
      setOriginalHiddenReportTableFacultyCellsMap(new Map(hiddenReportTableFacultyCellsMap));
    }
  }, [isOpen]);

  const handleChangeReportFacultyColumnOption = (event, cellObj = {}) => {
    const facultyId = cellObj?.id;
    if (!facultyId) {
      return;
    }
    const checked = !!event?.target?.checked;

    switch (facultyId) {
      case 'selectAll':
        if (checked) {
          // Select all - add all to hiddenReportTableFacultyCellsMap
          for (const cellObj of toJS(reportTableManager.reportTableFacultyCellsConfig)) {
            reportTableManager.addToHiddenReportTableFacultyCellsMap(cellObj);
          }
        } else {
          // Deselect all - clear all from hiddenReportTableFacultyCellsMap
          reportTableManager.clearHiddenReportTableFacultyCellsMap();
        }
        break;
      default:
        if (checked) {
          reportTableManager.addToHiddenReportTableFacultyCellsMap(cellObj);
        } else {
          reportTableManager.removeFromHiddenReportTableFacultyCellsMap(cellObj?.id);
        }
        break;
    }
  };

  const handleResetAllToOriginalSelections = () => {
    reportTableManager.setHiddenReportTableFacultyCellsMap(originalHiddenReportTableFacultyCellsMap);
  };

  const handleResetAllAsUnselected = () => {
    reportTableManager.clearHiddenReportTableFacultyCellsMap();
  };

  const renderHeaderCheckboxResetAllToOriginalSelectionsAction = () => {
    return (
      <div className='report-hide-columns-header-checkbox-reset-all-action reset-to-original-selections-action'
        onClick={handleResetAllToOriginalSelections}
        onKeyUp={(event) => {
          if (event?.key === 'Enter' || event?.key === ' ') {
            handleResetAllToOriginalSelections();
          }
        }}
        role='button'
        tabIndex={0}>
        {t('resetAllToOriginalSelections')}
      </div>
    );
  };

  const renderHeaderCheckboxResetAllAsUnselectedAction = () => {
    return (
      <div className='report-hide-columns-header-checkbox-reset-all-action reset-all-as-unselected-action'
        onClick={handleResetAllAsUnselected}
        onKeyUp={(event) => {
          if (event?.key === 'Enter' || event?.key === ' ') {
            handleResetAllAsUnselected();
          }
        }}
        role='button'
        tabIndex={0}>
        {t('resetAllAsUnselected')}
      </div>
    );
  };

  const handleCloseReportHideColumnsModal = async (_event, actionButtonData = {}) => {
    setIsLoadingReportHideColumnsModal(true);

    if (actionButtonData?.className?.includes?.(kebabCase(buttonKeyConfirmLabel))) {
      // user confirmed changes via 'Hide Columns' action button
      props.onClose?.();

      setIsLoadingReportHideColumnsModal(false);
    } else {
      // user did not confirm changes, so restore original selections
      reportTableManager.setHiddenReportTableFacultyCellsMap(originalHiddenReportTableFacultyCellsMap);

      props.onClose?.();

      setIsLoadingReportHideColumnsModal(false);
    }
  };

  const renderHeaderCheckboxControls = () => {
    const cellObj = {
      id: 'selectAll'
    };
    const checkboxLabel = t(cellObj.id);
    const isChecked = reportTableManager.isAllHiddenReportTableFacultyCells;
    const checkboxId = `report-faculty-checkbox-${cellObj?.id}`;

    return (
      <div className='report-hide-columns-header-checkbox-container'>
        <div className='report-hide-columns-header-checkbox-label-wrapper'>
          <IndeterminateCheckbox
            checked={isChecked}
            id={checkboxId}
            indeterminate={false}
            onChange={(event) => handleChangeReportFacultyColumnOption(event, cellObj)}
            value={cellObj?.id} />
          <label
            className={classNames('report-hide-columns-header-checkbox-select-all-label', { checked: isChecked })}
            htmlFor={checkboxId}>
            {checkboxLabel}
          </label>
        </div>
        {allowReportHideColumnsResetAllToOriginalSelectionsAction && renderHeaderCheckboxResetAllToOriginalSelectionsAction()}
        {allowReportHideColumnsResetAllAsUnselectedAction && renderHeaderCheckboxResetAllAsUnselectedAction()}
      </div>
    );
  };

  const renderReportFacultyColumnOption = (cellObj) => {
    const facultyLabelPropName = REPORT_FACULTY_TABLE_CELL_LABEL_KEY[activeReportFacultyType.toUpperCase()];

    const facultyId = cellObj?.id;

    const faculty = cellObj?.originalData;
    if (!facultyLabelPropName || !facultyId || !faculty) {
      return null;
    }
    let facultyName;
    if (facultyLabelPropName === REPORT_FACULTY_TABLE_CELL_LABEL_KEY.CLASSROOM) {
      facultyName = ReportIdentityService.getReportStudentName(faculty) || t('unknownFacultyName');
    } else {
      facultyName = faculty[facultyLabelPropName] || t('unknownFacultyName');
    }

    const leadTeacherName = isFacultySchoolReport ? (
      ReportIdentityService.getReportLeadTeacherName(faculty) || t('unknownLeadTeacher')
    ) : undefined;

    let checkboxLabel;
    if (leadTeacherName) {
      checkboxLabel = `${leadTeacherName} ‐ ${facultyName}`;
    } else {
      checkboxLabel = `${facultyName}`;
    }

    const isChecked = reportTableManager.hiddenReportTableFacultyCellsMap.has(facultyId);
    return (
      <div key={facultyId}
        className={classNames('report-hide-columns-modal-faculty-option-row', reportInfoClassNames)}>
        <div className={classNames('report-faculty-option-checkbox-wrapper', reportInfoClassNames)}>
          {renderCheckboxWithLabel({
            cellObj,
            isChecked,
            label: checkboxLabel
          })}
        </div>
      </div>
    );
  };

  const renderFacultyColumnOptions = () => {
    const facultyLabelPropName = REPORT_FACULTY_TABLE_CELL_LABEL_KEY[activeReportFacultyType.toUpperCase()];

    return (
      <>
        <div className='report-hide-columns-modal-body-top'>
          <div className='report-hide-columns-modal-body-top-default'>
            <div className='report-hide-columns-modal-body-header-label'>
              {t(`${facultyLabelPropName}HeaderLabel`)}
            </div>
          </div>
          <div className='report-hide-columns-modal-body-top-directions'>
            {t('hideColumnsBodyDirectionsMsg')}
          </div>
        </div>
        {renderHeaderCheckboxControls()}
        <div className='report-hide-columns-modal-faculty-options'>
          {toJS(reportTableManager.reportTableFacultyCellsConfig)?.map((cellObj) => {
            return renderReportFacultyColumnOption(cellObj);
          })}
        </div>
      </>
    );
  };

  const renderCheckboxWithLabel = ({ cellObj, isChecked, isDisabled = false, label } = {}) => {
    const checkboxId = `report-faculty-checkbox-${cellObj?.id}`;
    return (
      <>
        <IndeterminateCheckbox
          checked={isChecked}
          disabled={isDisabled}
          id={checkboxId}
          indeterminate={false}
          onChange={(event) => handleChangeReportFacultyColumnOption(event, cellObj)}
          value={cellObj?.id} />
        {PopupService.renderPopup({
          className: 'report-faculty-option-checkbox-popup',
          content: label,
          mouseEnterDelay: 1000,
          // offset: [-10, 0],
          position: 'top left',
          trigger: (
            <label
              className={classNames('report-faculty-option-checkbox-label', { checked: isChecked, disabled: isDisabled })}
              htmlFor={checkboxId}>
              {label}
            </label>
          ),
          wide: false
        })}
      </>
    );
  };

  const headerMsg = t('hideColumnsHeaderMsg');

  return (
    <GenericModal
      bodyMsg={renderFacultyColumnOptions}
      buttonKeyCancelLabel={buttonKeyCancelLabel}
      buttonKeyConfirmLabel={buttonKeyConfirmLabel}
      className={className}
      closeOnDimmerClick={closeOnDimmerClick}
      closeOnEscape={closeOnEscape}
      disableKeystroke={disableKeystroke}
      disableMove={disableMove}
      disableResize={disableResize}
      headerMsg={headerMsg}
      initHeight={initHeight || minHeight}
      initWidth={initWidth || minWidth}
      isLoading={isLoadingReportHideColumnsModal}
      isOpen={isOpen}
      onClose={handleCloseReportHideColumnsModal}
      onOpen={onOpen}
      onRequestClose={onRequestClose}
      overlay={overlay}
      top={top} />
  );
});

export default ReportHideColumnsModal;

SatCoreRegister('ReportHideColumnsModal', ReportHideColumnsModal);
